export class ArrowPointerService {
    /**
     * Add arrow pointer with year over year percentage and currently selected value
     * @param element The element where the chart will be rendered.
     * @param configuration The chart configuration.
     *
     * @returns {void}
     */
    addArrowPointer(chartInstance, element, configuration) {
        if (configuration.customArrowPointer?.show && configuration.series.length) {
            let dataIndex = configuration.series[0]['data'].length - 1;
            if (configuration.customArrowPointer.year) {
                const xAxisData = configuration.xAxis[0]?.data ?? configuration?.xAxis?.data ?? [];
                xAxisData.forEach((el, index) => {
                    if (el == configuration.customArrowPointer.year) {
                        dataIndex = index;
                    }
                });
            }
            chartInstance.dispatchAction({
                type: 'downplay',
            });
            // This adds circle to the last item from series
            chartInstance.dispatchAction({
                type: 'highlight',
                dataIndex: dataIndex,
            });
            const currentValue = configuration.series[0]['data'][dataIndex] ?? null;
            if (configuration.customArrowPointer?.version === 2) {
                const color = this.getTextColorBasedOnPercentage(currentValue);
                setTimeout(() => {
                    chartInstance.setOption({'emphasis': {itemStyle: {borderColor: color, borderWidth: 1.5}} });
                }, 100);
                this.appendCurrentYearDetails(element, configuration, currentValue, configuration.xAxis[0]?.data?.[dataIndex] ?? configuration.xAxis.data[dataIndex] ?? null, color);
            } else {
                const percentageDiff = this.calculateYearOverYearPercentage(
                    currentValue,
                    configuration.series[0]['data'][dataIndex - 1] ?? null
                );
                this.appendArrowPointer(element, configuration, currentValue, percentageDiff);
            }
            // When the user leaves chart return highlight to the last item in the dataset.
            const chartElement = element.querySelector('svg') ?? element.querySelector('canvas');
            chartElement.removeEventListener('mouseleave', this.mouseLeaveEventListener);
            chartElement.addEventListener(
                'mouseleave',
                this.mouseLeaveEventListener.bind(this, chartInstance, element, configuration, dataIndex)
            );
        }
    }

    /**
     * This is triggered when mouse leaves chart container so the last item can be highlighted again
     * @param element The element where the chart will be rendered.
     * @param configuration The chart configuration.
     * @param dataIndex currently focused item
     *
     * @returns {void}
     */
    mouseLeaveEventListener(chartInstance, element, configuration, dataIndex) {
        setTimeout(() => {
            chartInstance.dispatchAction({
                type: 'downplay',
            });
            chartInstance.dispatchAction({
                type: 'highlight',
                dataIndex: dataIndex,
            });
            const currentValue = chartInstance.getOption().series[0]['data'][dataIndex] ?? null;
            if (configuration.customArrowPointer?.version === 2) {
                const color = this.getTextColorBasedOnPercentage(currentValue);
                setTimeout(() => {
                    chartInstance.setOption({'emphasis': {itemStyle: {borderColor: color, borderWidth: 1.5}} });
                }, 100);
                this.appendCurrentYearDetails(element, configuration, currentValue, configuration.xAxis.data[dataIndex] ?? null, color);
            } else {
                const percentageDiff = this.calculateYearOverYearPercentage(
                    currentValue,
                    configuration.series[0]['data'][dataIndex - 1] ?? null
                );
                this.appendArrowPointer(element, configuration, currentValue, percentageDiff);
            }
        }, 100);
    }

    /**
     * Append to element arrow pointer with percentage and currently selected value
     * @param element The element where the chart will be rendered.
     * @param configuration The chart configuration.
     * @param currentValue Value of the currently selected data item.
     * @param percentageDiff Difference between currently selected data item and data item from the year before.
     *
     * @returns {void}
     */
    appendArrowPointer(element, configuration, currentValue, percentageDiff) {
        let arrowPointer;
        if (percentageDiff === null || percentageDiff === 0) {
            arrowPointer = {
                imagePath: '',
                labelClass: 'text-scaler-black',
            };
        } else if (configuration.customArrowPointer.direction == 'up' && parseFloat(percentageDiff) >= 0) {
            arrowPointer = {
                imagePath: '/img/arrow-up-right-green.svg',
                labelClass: 'text-scaler-light-green',
            };
        } else if (configuration.customArrowPointer.direction == 'up' && parseFloat(percentageDiff) < 0) {
            arrowPointer = {
                imagePath: '/img/arrow-down-right-red.svg',
                labelClass: 'text-scaler-red-light',
            };
        } else if (configuration.customArrowPointer.direction == 'down' && parseFloat(percentageDiff) < 0) {
            arrowPointer = {
                imagePath: '/img/arrow-down-right-green.svg',
                labelClass: 'text-scaler-light-green',
            };
        } else if (configuration.customArrowPointer.direction == 'down' && parseFloat(percentageDiff) >= 0) {
            arrowPointer = {
                imagePath: '/img/arrow-up-right-red.svg',
                labelClass: 'text-scaler-red-light',
            };
        }

        const showInlineArrowPointer = configuration.customArrowPointer.showInline;
        element = showInlineArrowPointer ? element.parentNode : element;
        // Arrow pointer exists so just update values
        if (element.querySelector('.current-value')) {
            element.querySelector('.current-value').innerHTML = currentValue !== null ? currentValue : '-';
            element.querySelector('.arrow-pointer').innerHTML = `
                <img src="${arrowPointer.imagePath}"/>
                <span class="${showInlineArrowPointer ? 'text-xs ' : ''}${arrowPointer.labelClass}">${
                percentageDiff !== null ? percentageDiff : '-'
            }</span>
            `;
            return;
        }

        const arrowPointerContainer = document.createElement('div');
        arrowPointerContainer.innerHTML = `
            <div class="current-value font-bold">
                ${currentValue !== null ? currentValue : '-'}
            </div>
            <div class="arrow-pointer inline-flex items-center justify-between min-h-[17px]">
                <img src="${arrowPointer.imagePath ?? '#'}"/>
                <span class="${showInlineArrowPointer ? 'text-xs ' : ''}${arrowPointer.labelClass}">${
            percentageDiff !== null ? percentageDiff : '-'
        }</span>
            </div>
        `;
        arrowPointerContainer.classList.add('inline-flex', 'w-full', 'justify-between', 'items-center');
        if (configuration.customArrowPointer.class) {
            arrowPointerContainer.classList.add(...configuration.customArrowPointer.class);
        }
        if (showInlineArrowPointer) {
            element.append(arrowPointerContainer);
            return;
        }
        element.prepend(arrowPointerContainer);
    }

    /**
     * Get difference between current and previous year
     * @param currentValue Value of the currently selected data item.
     * @param previousYearValue Value of the data item before currently selected data item.
     *
     * @returns {float|null}
     */
    calculateYearOverYearPercentage(currentValue, previousYearValue) {
        if (!currentValue || !previousYearValue || currentValue == 0) {
            return null;
        }

        return Math.round((currentValue - previousYearValue) * 100) / 100;
    }

    getTextColorBasedOnPercentage(percentage) {
        if (percentage > 79) {
            // green
            return '#16A34A';
        }
        if (percentage > 49) {
            // yellow
            return '#EAB308';
        }
        if (percentage > 24) {
            // orange
            return '#EA580C';
        }
        //red
        return '#DC2626';
    }

    /**
     * Append to element arrow pointer with percentage and currently selected value
     * @param element The element where the chart will be rendered.
     * @param configuration The chart configuration.
     * @param currentValue Value of the currently selected data item.
     * @param label
     *
     * @returns {void}
     */
    appendCurrentYearDetails(element, configuration, currentValue, label, color) {
        // Arrow pointer exists so just update values
        if (element.querySelector('.current-value')) {
            element.querySelector('.current-value').style.color = color;
            element.querySelector('.current-value').innerHTML = currentValue !== null ? currentValue + '%' : '-';
            element.querySelector('.current-label').innerHTML = label;
            return;
        }
        const currentYearDetailsContainer = document.createElement('div');
        currentYearDetailsContainer.innerHTML = `
            <div class="inline-flex justify-between items-center w-full mb-6 pl-4">
                <div class="font-bold leading-5.5">Progress</div>
                <div class="inline-flex justify-between items-center text-xs font-bold leading-[19px] rounded border-1 gap-x-2 border-scaler-gray-light py-1.5 px-2.5">
                    <div class="current-value" style="color: ${color};">
                        ${currentValue !== null ? currentValue + '%' : '-'}
                    </div>
                    <div class="text-scaler-gray current-label">
                        ${label !== null ? label : '-'}
                    </div>
                </div>
            </div>
        `;
        currentYearDetailsContainer.classList.add('inline-flex', 'w-full', 'justify-between', 'items-center');
        if (configuration.customArrowPointer?.class) {
            currentYearDetailsContainer.classList.add(...configuration.customArrowPointer.class);
        }

        element.prepend(currentYearDetailsContainer);
    }
}
